import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  SESSION_INACTIVITY_TIMEOUT_MS,
  clearLastActivityAt,
  isSessionInactive,
  readLastActivityAt,
  touchLastActivity,
} from '../../utils/sessionInactivity';
import { isAuthEntryRoute } from '../../utils/authRouting';

const CHECK_INTERVAL_MS = 60 * 1000;

const InactivityLogoutGuard: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggingOutRef = React.useRef(false);

  React.useEffect(() => {
    if (!isAuthenticated) {
      clearLastActivityAt();
      isLoggingOutRef.current = false;
      return;
    }

    let cancelled = false;
    let intervalId = 0;

    const markActivity = () => {
      if (isLoggingOutRef.current || cancelled) return;
      touchLastActivity();
    };

    const maybeLogoutForInactivity = async (): Promise<boolean> => {
      if (isLoggingOutRef.current || cancelled) return false;
      const lastActivityAt = readLastActivityAt();
      if (!isSessionInactive(lastActivityAt, Date.now(), SESSION_INACTIVITY_TIMEOUT_MS)) {
        return false;
      }

      isLoggingOutRef.current = true;
      try {
        await logout();
      } catch {
        // Force logout path still clears state.
      } finally {
        clearLastActivityAt();
        if (!cancelled && !isAuthEntryRoute()) {
          navigate('/login', { replace: true });
        }
      }
      return true;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      // Check idle BEFORE rewriting activity — otherwise a cold return after
      // days looks fresh and never logs out.
      void (async () => {
        const loggedOut = await maybeLogoutForInactivity();
        if (!loggedOut) markActivity();
      })();
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    const startWatching = () => {
      activityEvents.forEach((eventName) => {
        window.addEventListener(eventName, markActivity, { passive: true });
      });
      document.addEventListener('visibilitychange', onVisibilityChange);

      intervalId = window.setInterval(() => {
        void maybeLogoutForInactivity();
      }, CHECK_INTERVAL_MS);
    };

    // Expire synchronously on mount before any activity write can refresh the clock.
    void (async () => {
      const loggedOut = await maybeLogoutForInactivity();
      if (cancelled || loggedOut) return;
      startWatching();
    })();

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, markActivity);
      });
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAuthenticated, logout, navigate, location.pathname, location.hash]);

  return null;
};

export default InactivityLogoutGuard;
